import type { ChartConfig, ChartSpec } from './chartUtils';

/** Build a standalone HTML file from the chart spec for offline viewing. */
export function buildStandaloneHtml(spec: ChartSpec, config: ChartConfig): string {
  const dataJson = JSON.stringify(spec.data, null, 2);
  const configJson = JSON.stringify(config, null, 2);

  if (spec.chart_type === 'map') {
    return buildStandaloneMapHtml(spec, dataJson, configJson);
  }

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>${spec.title}</title>
<script src="https://cdn.jsdelivr.net/npm/chart.js"></script>
<style>
  body { margin: 0; padding: 24px; background: #1a1a1a; color: #e5e5e5; font-family: system-ui, sans-serif; }
  h2 { margin: 0 0 4px; font-size: 18px; }
  .subtitle { color: #999; font-size: 13px; margin-bottom: 16px; }
  .chart-wrap { max-width: 900px; margin: 0 auto; }
  canvas { width: 100% !important; }
</style>
</head>
<body>
<div class="chart-wrap">
  <h2>${spec.title}</h2>
  ${spec.subtitle ? `<div class="subtitle">${spec.subtitle}</div>` : ''}
  <canvas id="chart"></canvas>
</div>
<script>
const DATA = ${dataJson};
const CONFIG = ${configJson};
const ctx = document.getElementById('chart').getContext('2d');

const chartType = '${spec.chart_type}';
const COLORS = CONFIG.colors || ['#ff6738','#3b82f6','#22c55e','#f59e0b','#8b5cf6','#ec4899','#06b6d4','#ef4444','#84cc16','#f97316'];

if (chartType === 'pie') {
  const labelKey = CONFIG.label_key || Object.keys(DATA[0])[0];
  const valueKey = CONFIG.value_key || Object.keys(DATA[0])[1];
  new Chart(ctx, {
    type: 'pie',
    data: {
      labels: DATA.map(d => d[labelKey]),
      datasets: [{ data: DATA.map(d => d[valueKey]), backgroundColor: COLORS }]
    },
    options: { plugins: { legend: { labels: { color: '#ccc' } } } }
  });
} else if (chartType === 'line') {
  const xKey = CONFIG.x_key || Object.keys(DATA[0])[0];
  const yKey = CONFIG.y_key || Object.keys(DATA[0])[1];
  new Chart(ctx, {
    type: 'line',
    data: {
      labels: DATA.map(d => d[xKey]),
      datasets: [{ label: CONFIG.y_label || yKey, data: DATA.map(d => d[yKey]), borderColor: CONFIG.color || '#ff6738', tension: 0.3 }]
    },
    options: { scales: { x: { ticks: { color: '#999' } }, y: { ticks: { color: '#999' } } }, plugins: { legend: { labels: { color: '#ccc' } } } }
  });
} else {
  const keys = Object.keys(DATA[0] || {});
  let catKey = CONFIG.x_key || keys[0];
  let valKey = CONFIG.y_key || keys[1];
  if (typeof DATA[0]?.[catKey] === 'number' && typeof DATA[0]?.[valKey] !== 'number') {
    [catKey, valKey] = [valKey, catKey];
  }
  const isHorizontal = chartType === 'horizontal_bar';
  new Chart(ctx, {
    type: 'bar',
    data: {
      labels: DATA.map(d => d[catKey]),
      datasets: [{ label: CONFIG.y_label || valKey, data: DATA.map(d => d[valKey]), backgroundColor: DATA.map((_, i) => COLORS[i % COLORS.length]) }]
    },
    options: { indexAxis: isHorizontal ? 'y' : 'x', scales: { x: { ticks: { color: '#999' } }, y: { ticks: { color: '#999' } } }, plugins: { legend: { labels: { color: '#ccc' } } } }
  });
}
</script>
</body>
</html>`;
}

/** Build a standalone HTML file with a Leaflet map. */
function buildStandaloneMapHtml(spec: ChartSpec, dataJson: string, configJson: string): string {
  const escTitle = spec.title.replace(/</g, '&lt;');
  const escSubtitle = spec.subtitle ? spec.subtitle.replace(/</g, '&lt;') : '';
  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>${escTitle}</title>
<link rel="stylesheet" href="https://unpkg.com/leaflet@1.9.4/dist/leaflet.css" />
<script src="https://unpkg.com/leaflet@1.9.4/dist/leaflet.js"></script>
<style>
  body { margin: 0; padding: 0; background: #1a1a1a; color: #e5e5e5; font-family: system-ui, sans-serif; }
  .header { padding: 16px 24px 8px; }
  h2 { margin: 0 0 4px; font-size: 18px; }
  .subtitle { color: #999; font-size: 13px; }
  .legend { display: flex; flex-wrap: wrap; gap: 12px; padding: 8px 24px; font-size: 12px; }
  .legend-item { display: flex; align-items: center; gap: 4px; }
  .legend-dot { width: 10px; height: 10px; border-radius: 50%; display: inline-block; }
  .count { padding: 4px 24px 8px; font-size: 12px; color: #999; }
  #map { width: 100%; height: calc(100vh - 100px); min-height: 400px; }
</style>
</head>
<body>
<div class="header">
  <h2>${escTitle}</h2>
  ${escSubtitle ? `<div class="subtitle">${escSubtitle}</div>` : ''}
</div>
<div id="legend" class="legend"></div>
<div id="count" class="count"></div>
<div id="map"></div>
<script>
const DATA = ${dataJson};
const CONFIG = ${configJson};
const COLORS = CONFIG.colors || ['#ff6738','#3b82f6','#22c55e','#f59e0b','#8b5cf6','#ec4899','#06b6d4','#ef4444','#84cc16','#f97316'];
const defaultColor = CONFIG.color || '#ff6738';

const latKey = CONFIG.lat_key || 'latitude';
const lngKey = CONFIG.lng_key || 'longitude';
const nameKey = CONFIG.name_key || 'name';
const popupKeys = CONFIG.popup_keys;
const markerColorKey = CONFIG.marker_color_key;

const valid = DATA.filter(d => typeof d[latKey] === 'number' && typeof d[lngKey] === 'number' && !isNaN(d[latKey]) && !isNaN(d[lngKey]));

if (valid.length === 0) {
  document.getElementById('map').innerHTML = '<div style="padding:24px;color:#999;">No valid coordinates found.</div>';
} else {
  // Color map
  const colorMap = new Map();
  if (markerColorKey) {
    const seen = [];
    valid.forEach(d => {
      const v = String(d[markerColorKey] ?? '');
      if (!colorMap.has(v)) { colorMap.set(v, COLORS[seen.length % COLORS.length]); seen.push(v); }
    });
    const legendEl = document.getElementById('legend');
    colorMap.forEach((hex, val) => {
      legendEl.innerHTML += '<span class="legend-item"><span class="legend-dot" style="background:' + hex + '"></span><span>' + val + '</span></span>';
    });
  }

  document.getElementById('count').textContent = valid.length.toLocaleString() + ' markers';

  // Center & zoom
  const avgLat = valid.reduce((s, d) => s + d[latKey], 0) / valid.length;
  const avgLng = valid.reduce((s, d) => s + d[lngKey], 0) / valid.length;
  const lats = valid.map(d => d[latKey]);
  const lngs = valid.map(d => d[lngKey]);
  const spread = Math.max(Math.max(...lats) - Math.min(...lats), Math.max(...lngs) - Math.min(...lngs));
  let zoom = 13;
  if (spread > 10) zoom = 4;
  else if (spread > 5) zoom = 6;
  else if (spread > 2) zoom = 7;
  else if (spread > 0.5) zoom = 10;
  else if (spread > 0.1) zoom = 12;

  const map = L.map('map').setView([avgLat, avgLng], zoom);
  L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
  }).addTo(map);

  valid.forEach((d, i) => {
    const fill = markerColorKey && colorMap.has(String(d[markerColorKey] ?? ''))
      ? colorMap.get(String(d[markerColorKey] ?? ''))
      : defaultColor;

    const marker = L.circleMarker([d[latKey], d[lngKey]], {
      radius: 5, color: '#fff', weight: 1, fillColor: fill, fillOpacity: 0.8
    }).addTo(map);

    let popupHtml = '<strong>' + (d[nameKey] || ('Point ' + (i+1))) + '</strong>';
    if (popupKeys) {
      popupKeys.filter(k => k !== nameKey && d[k] != null).forEach(k => {
        popupHtml += '<div style="font-size:12px">' + k + ': ' + d[k] + '</div>';
      });
    } else {
      Object.entries(d).filter(([k]) => k !== latKey && k !== lngKey && k !== nameKey).slice(0, 5).forEach(([k, v]) => {
        popupHtml += '<div style="font-size:12px">' + k + ': ' + v + '</div>';
      });
    }
    marker.bindPopup(popupHtml);
  });
}
</script>
</body>
</html>`;
}
